import { useAtomValue } from "@effect/atom-react";
import { useCallback, useMemo } from "react";

import type { CreateThreadInput } from "@cleo/client-runtime/operations";
import type { EnvironmentId, ThreadId } from "@cleo/contracts";

import {
  inferProjectTitleFromPath,
  normalizeProjectPathForComparison,
  normalizeProjectPathForDispatch,
  resolveProjectPathForDispatch,
} from "@cleo/client-runtime/state/projects";

import { sortScopedProjectsForSidebar } from "../components/Sidebar.logic";
import { newProjectId } from "../lib/utils";
import { resolveDefaultProviderModelSelection } from "../providerInstances";
import { createdRootShellPresent, waitForCreatedRootShell } from "../rootSessionBirth";
import { useProjects, useThreadShells } from "../state/entities";
import { useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import { projectEnvironment } from "../state/projects";
import { primaryServerProvidersAtom } from "../state/server";
import { threadEnvironment } from "../state/threads";
import { useAtomCommand } from "../state/use-atom-command";
import { useAtomQueryRunner } from "../state/use-atom-query-runner";
import { filesystemEnvironment } from "../state/filesystem";
import type { WorldSessionLaunchProject } from "./worldSessionFirstExecution";

/**
 * Everything a bare World Session needs to launch its first execution, read
 * from the project record and the environment's live providers — never from
 * another execution. One module so the shell tests can stub it whole.
 */
export function useWorldSessionLaunchProjects(): ReadonlyArray<WorldSessionLaunchProject> {
  const projects = useProjects();
  const threads = useThreadShells();
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryProviders = useAtomValue(primaryServerProvidersAtom);
  return useMemo(() => {
    const ordered = sortScopedProjectsForSidebar(projects, threads, "updated_at");
    return ordered.map((project) => {
      const providers =
        environments.find((environment) => environment.environmentId === project.environmentId)
          ?.serverConfig?.providers ??
        (project.environmentId === primaryEnvironmentId ? primaryProviders : []);
      return {
        environmentId: project.environmentId,
        projectId: project.id,
        title: project.title,
        workspaceRoot: project.workspaceRoot,
        modelSelection: resolveDefaultProviderModelSelection(
          providers,
          project.defaultModelSelection,
        ),
      };
    });
  }, [environments, primaryEnvironmentId, primaryProviders, projects, threads]);
}

/**
 * The Cowork lane (principal ruling 2026-08-28): a World session without a
 * bound default project runs in the World's Files folder. A project IS a
 * folder pointer here, so this resolves the deterministic folder-project for
 * that root — reusing the environment's project whose workspaceRoot already
 * is the root, else creating one visibly in the projects list. Returns null
 * only when creation fails; it never picks any other folder.
 */
export function useEnsureWorldFilesProject(): (input: {
  readonly environmentId: EnvironmentId;
  readonly filesRoot: string;
  readonly projects: ReadonlyArray<WorldSessionLaunchProject>;
  /** Names the minted folder-project by World, never a bare basename. */
  readonly worldName?: string;
}) => Promise<WorldSessionLaunchProject | null> {
  const createProject = useAtomCommand(projectEnvironment.create, { reportFailure: false });
  const browse = useAtomQueryRunner(filesystemEnvironment.browse, { reportFailure: false });
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryProviders = useAtomValue(primaryServerProvidersAtom);
  return useCallback(
    async ({ environmentId, filesRoot, projects, worldName }) => {
      let workspaceRoot = normalizeProjectPathForDispatch(filesRoot);
      if (workspaceRoot.startsWith("~/")) {
        // The host stores absolute project roots. Resolve its home through
        // its existing filesystem door; the browser's home may be elsewhere.
        const home = await browse({ environmentId, input: { partialPath: "~/" } });
        if (home._tag === "Failure") return null;
        workspaceRoot = resolveProjectPathForDispatch(
          `./${workspaceRoot.slice(2)}`,
          home.value.parentPath,
        );
      }
      const existing =
        projects.find(
          (project) =>
            project.environmentId === environmentId &&
            normalizeProjectPathForComparison(project.workspaceRoot) ===
              normalizeProjectPathForComparison(workspaceRoot),
        ) ?? null;
      if (existing !== null) return existing;
      const providers =
        environments.find((environment) => environment.environmentId === environmentId)
          ?.serverConfig?.providers ??
        (environmentId === primaryEnvironmentId ? primaryProviders : []);
      const modelSelection = resolveDefaultProviderModelSelection(providers, null);
      const projectId = newProjectId();
      const base = inferProjectTitleFromPath(workspaceRoot);
      const title = worldName?.trim() ? `${worldName.trim()} · ${base}` : base;
      const created = await createProject({
        environmentId,
        input: {
          projectId,
          title,
          workspaceRoot,
          createWorkspaceRootIfMissing: true,
          defaultModelSelection: modelSelection,
        },
      });
      if (created._tag === "Failure") return null;
      return { environmentId, projectId, title, workspaceRoot, modelSelection };
    },
    [browse, createProject, environments, primaryEnvironmentId, primaryProviders],
  );
}

export function useWorldSessionExecutionDispatch(): {
  readonly dispatch: (
    environmentId: EnvironmentId,
    input: CreateThreadInput,
  ) => Promise<{ readonly ok: boolean }>;
  readonly executionArrived: (environmentId: EnvironmentId, threadId: ThreadId) => Promise<boolean>;
  /** The same shell, read now: never waits (W-4703a3f5). */
  readonly executionPresent: (environmentId: EnvironmentId, threadId: ThreadId) => boolean;
} {
  const createThread = useAtomCommand(threadEnvironment.create, { reportFailure: false });
  const dispatch = useCallback(
    (environmentId: EnvironmentId, input: CreateThreadInput) =>
      createThread({ environmentId, input }).then((result) => ({
        ok: result._tag !== "Failure",
      })),
    [createThread],
  );
  return useMemo(
    () => ({
      dispatch,
      executionArrived: waitForCreatedRootShell,
      executionPresent: createdRootShellPresent,
    }),
    [dispatch],
  );
}
